/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { MessageSeverity, MessageVariant } from './Message.types';
import { MessageConfirmationIcon } from '../PRIVATE_ThemedIcons/MessageConfirmationIcon';
import { MessageErrorIcon } from '../PRIVATE_ThemedIcons/MessageErrorIcon';
import { MessageInfoIcon } from '../PRIVATE_ThemedIcons/MessageInfoIcon';
import { MessageWarningIcon } from '../PRIVATE_ThemedIcons/MessageWarningIcon';
import { Flex } from '../UNSAFE_Flex';
import {
  messageStartIconStyles,
  severityIconStyles,
  startIconContainerStyles
} from './themes/MessageStyles.css';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';

const severityIcons = {
  confirmation: MessageConfirmationIcon,
  error: MessageErrorIcon,
  info: MessageInfoIcon,
  warning: MessageWarningIcon
};

/**
 * Props for the StartIcon component
 */
type Props = {
  /**
   * The icon severity
   */
  severity: Exclude<MessageSeverity, 'none'>;

  /**
   * Style variant
   */
  variant?: MessageVariant;
};

/**
 * StartIcon Component for rendering the severity based icon in Message
 */
function MessageStartIcon({ severity, variant = 'banner' }: Props) {
  const IconComponent = severityIcons[severity];
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const labelMap = {
    confirmation: translations.message_confirmation(),
    error: translations.message_error(),
    info: translations.message_info(),
    warning: translations.message_warning()
  };
  // TODO: JET-50793
  const iconContent =
    variant !== 'inline' ? (
      <Flex align="center" height="100%">
        <span class={startIconContainerStyles[variant]} role="img" aria-label={labelMap[severity]}>
          <IconComponent />
        </span>
      </Flex>
    ) : (
      <span class={startIconContainerStyles.inline} role="img" aria-label={labelMap[severity]}>
        <IconComponent />
      </span>
    );
  // If detail content is rendered, then wrap it in a div with specified style classes
  return (
    <div
      class={classNames([
        messageStartIconStyles.base,
        messageStartIconStyles[variant],
        severityIconStyles[variant]
      ])}
      role="presentation">
      {iconContent}
    </div>
  );
}

export { MessageStartIcon };
